import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cancelCoachingAIJob, createCoachingAIJob, createCoachingActivity, createCoachingCapture, getCoachingActivities, getCoachingActivityFocuses, getCoachingAIJob, getCoachingAIJobResult, resolveCoachingActivityFocus, selectCoachingActivityFocus } from '@/lib/api';
import { useScopedKey, invalidateScope } from '@/lib/queryClient';
import { isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import type { CoachingAIJobProgress, CreateCoachingAIJobRequest, CreateCoachingActivityRequest } from '@/lib/types';
import { isRunning } from './assistPresentation';

export function assertAssistOwner(owner: DraftContext): void {
  if (!isCurrentDraftContext(owner)) throw new Error('Coaching context changed');
}
/** Check at actual dispatch and settlement, including after preceding async work.
 * Old 401s become inert context errors before reaching the global query handler. */
export async function ownedRequest<T>(owner: DraftContext, request: () => Promise<T>): Promise<T> {
  assertAssistOwner(owner);
  try { const result = await request(); assertAssistOwner(owner); return result; }
  catch (error) { assertAssistOwner(owner); throw error; }
}
export function useAssistContext(owner: DraftContext, playerIds: string[], visible = true) {
  const client = useQueryClient();
  const scope = { teamId: owner.teamId, seasonId: owner.seasonId };
  const alive = useRef(true);
  const instance = useRef(crypto.randomUUID());
  const playerKey = JSON.stringify(playerIds);
  const currentPlayers = useRef(playerKey); currentPlayers.current = playerKey;
  const [jobsByPlayers, setJobsByPlayers] = useState<Record<string, CoachingAIJobProgress>>({});
  const jobsRef = useRef(jobsByPlayers); jobsRef.current = jobsByPlayers;
  const job = jobsByPlayers[playerKey];
  const setJob = (value: CoachingAIJobProgress | undefined, players = currentPlayers.current, expectedId?: string) => setJobsByPlayers(previous => {
    // Recheck against queued state, not only the last rendered ref: another
    // admission may already have enqueued a replacement in this React batch.
    if (expectedId !== undefined && previous[players]?.id !== expectedId) return previous;
    if (previous[players] === value) return previous;
    const next = { ...previous };
    if (value) next[players] = value; else delete next[players];
    return next;
  });
  const activityRequest = useRef<CreateCoachingActivityRequest | null>(null);
  const admissionRequests = useRef(new Map<string, CreateCoachingAIJobRequest>());
  const [admissionErrors, setAdmissionErrors] = useState<Record<string, Error>>({});
  const [activityId, setActivityId] = useState('');
  const activitiesKey = useScopedKey('coach-assist-activities', { author: owner.actorId });
  const focusKey = useScopedKey('coach-assist-focuses', { activityId, author: owner.actorId });
  const jobKey = useScopedKey('coach-assist-job', { id: job?.id, instance: instance.current });
  const resultKey = useScopedKey('coach-assist-result', { id: job?.id, instance: instance.current });
  const activities = useQuery({ queryKey: activitiesKey, queryFn: ({ signal }) => ownedRequest(owner, () => getCoachingActivities(scope, signal)), enabled: visible, retry: false });
  const focuses = useQuery({ queryKey: focusKey, queryFn: ({ signal }) => ownedRequest(owner, () => getCoachingActivityFocuses(activityId, scope, signal)), enabled: visible && !!activityId, retry: false });
  const progress = useQuery({ queryKey: jobKey,
    queryFn: ({ signal }) => ownedRequest(owner, () => getCoachingAIJob(job!.id, scope, signal)),
    enabled: visible && !!job && isRunning(job), retry: false,
    refetchInterval: query => visible && !query.state.error && isRunning(query.state.data?.job ?? job) ? 1500 : false,
    refetchOnWindowFocus: false,
  });
  // Retain only owned job metadata across player switches. Private progress/
  // result query payloads still leave the cache on every key change below.
  useEffect(() => {
    const latest = progress.data?.job;
    if (latest && alive.current && isCurrentDraftContext(owner) && jobsRef.current[playerKey]?.id === latest.id) setJob(latest, playerKey, latest.id);
  }, [progress.data, playerKey, owner]);
  const currentJob = progress.data?.job ?? job;
  const result = useQuery({ queryKey: resultKey,
    queryFn: ({ signal }) => ownedRequest(owner, () => getCoachingAIJobResult(job!.id, scope, signal)),
    enabled: visible && currentJob?.status === 'needs_review' && currentJob.result_available, retry: false,
    staleTime: 0, refetchOnWindowFocus: visible,
  });
  const operation = useMutation({
    meta: { composerContext: owner }, retry: false,
    mutationFn: (request: CreateCoachingAIJobRequest) => ownedRequest(owner, () => createCoachingAIJob(request, scope)),
    onSuccess: (data, request) => { if (alive.current && isCurrentDraftContext(owner)) {
      const players = JSON.stringify(request.evidence.player_ids);
      setAdmissionErrors(previous => { const next = { ...previous }; delete next[players]; return next; });
      if (admissionRequests.current.get(players)?.client_operation_id === request.client_operation_id) setJob(data.job, players);
    } },
    onError: (error, request) => { if (alive.current && isCurrentDraftContext(owner)) {
      const players = JSON.stringify(request.evidence.player_ids);
      setAdmissionErrors(previous => ({ ...previous, [players]: error }));
    } },
  });
  const cancel = useMutation({ meta: { composerContext: owner }, retry: false,
    mutationFn: (input: { id: string; players: string; jobKey: readonly unknown[]; resultKey: readonly unknown[] }) => ownedRequest(owner, () => cancelCoachingAIJob(input.id, scope)),
    onSuccess: async (data, input) => {
      await client.cancelQueries({ queryKey: input.jobKey, exact: true });
      client.removeQueries({ queryKey: input.jobKey, exact: true });
      client.removeQueries({ queryKey: input.resultKey, exact: true });
      if (alive.current && isCurrentDraftContext(owner) && jobsRef.current[input.players]?.id === input.id) setJob(data.job, input.players, input.id);
    },
  });
  const newActivity = useMutation({ meta: { composerContext: owner }, retry: false,
    mutationFn: (request: CreateCoachingActivityRequest) => ownedRequest(owner, () => createCoachingActivity(request, scope)),
    onSuccess: (data, request) => { if (alive.current && isCurrentDraftContext(owner)) {
      if (activityRequest.current?.client_operation_id === request.client_operation_id) activityRequest.current = null;
      setActivityId(data.activity.id); void activities.refetch();
    } },
  });
  const capture = useMutation({ meta: { composerContext: owner }, retry: false,
    mutationFn: (input: { text: string; activityId: string; playerIds: string[]; occurredAt: string; operationId: string }) => ownedRequest(owner, () => createCoachingCapture({ activity_id: input.activityId, kind: 'text', text: input.text, player_ids: input.playerIds, occurred_at: input.occurredAt, client_operation_id: input.operationId }, scope)),
  });
  const select = useMutation({ meta: { composerContext: owner }, retry: false,
    mutationFn: async (input: { source_kind: 'note' | 'goal'; source_id: number; player_id: string; activityId: string; expectedRevision: number; operationId: string }) => {
      const proof = await ownedRequest(owner, () => resolveCoachingActivityFocus(input.activityId, { source_kind: input.source_kind, source_id: input.source_id, player_id: input.player_id }, scope));
      return ownedRequest(owner, () => selectCoachingActivityFocus(input.activityId, input.player_id, {
        reference: proof.reference, expected_revision: input.expectedRevision, client_operation_id: input.operationId,
      }, scope));
    },
    onSuccess: () => { if (alive.current && isCurrentDraftContext(owner)) { void focuses.refetch(); invalidateScope(); } },
    onError: () => { if (alive.current && isCurrentDraftContext(owner)) void focuses.refetch(); },
  });
  // Freeze each previous request's keys for cleanup; never derive cleanup from
  // the new owner's stores. Cancelling queries prevents late private settlement.
  useEffect(() => () => {
    void client.cancelQueries({ queryKey: jobKey, exact: true });
    void client.cancelQueries({ queryKey: resultKey, exact: true });
    client.removeQueries({ queryKey: jobKey, exact: true });
    client.removeQueries({ queryKey: resultKey, exact: true });
  }, [client, JSON.stringify(jobKey), JSON.stringify(resultKey)]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (!visible) {
      void client.cancelQueries({ queryKey: jobKey, exact: true });
      void client.cancelQueries({ queryKey: resultKey, exact: true });
    }
  }, [visible, client, JSON.stringify(jobKey), JSON.stringify(resultKey)]);
  const operationError = admissionErrors[playerKey] ?? null;
  const generate = (task: 'player_preparation' | 'next_session_preparation') => {
    if (!visible || operation.isPending || operationError || isRunning(currentJob) || (currentJob && !currentJob.can_regenerate)) return;
    assertAssistOwner(owner);
    // Explicit new operation only. No automatic resubmission on any error.
    operation.reset();
    setJob(undefined);
    const request: CreateCoachingAIJobRequest = { client_operation_id: crypto.randomUUID(), evidence: {
      task, draft_target: 'what_to_do_next', audience: 'coach', publication_class: 'creator_private', selection_mode: 'automatic_history', player_ids: playerIds,
    } };
    admissionRequests.current.set(playerKey, request); operation.mutate(request);
  };
  const createActivityContext = () => {
    if (newActivity.isPending) return;
    assertAssistOwner(owner);
    activityRequest.current ??= { kind: 'training', occurred_at: new Date().toISOString(), client_operation_id: crypto.randomUUID() };
    newActivity.mutate(activityRequest.current);
  };
  const reconcileAdmission = () => {
    const request = admissionRequests.current.get(playerKey);
    if (!visible || !request || operation.isPending || JSON.stringify(request.evidence.player_ids) !== playerKey) return;
    assertAssistOwner(owner); operation.mutate(request);
  };
  return { activities, activityId, setActivityId, focuses, newActivity, createActivityContext, capture, select,
    generate, reconcileAdmission, canReconcile: !!operationError, operationError, operation, cancel, cancelPreparation: () => { if (job) cancel.mutate({ id: job.id, players: playerKey, jobKey, resultKey }); }, job: currentJob, progress, result };
}
